import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import { matchSorter } from "match-sorter";
import { append, ascend, find, join, prop, reject, sortWith } from "ramda";
import { isBlank, notBlank } from "../../../util/ext";
import { useSubdivisions } from "../../vocabularyServices";
import VirtualizedListbox, {
  LISTBOX_BORDER_STYLE,
} from "../../../components/VirtualizedListbox";

// e.g. "New York, US"
const regionLabel = ({ name, countryCode }) =>
  join(", ", reject(isBlank, [name, countryCode]));

// Region names repeat across countries, e.g. Punjab in IN and PK
const isSameRegion = (a, b) =>
  a.name === b.name && a.countryCode === b.countryCode;

const filterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: [regionLabel, "name", "countryCode"],
  });

const SubmissionRegionSelect = ({ region, country, onChange }) => {
  const subdivisions = useSubdivisions();

  if (subdivisions.isLoading) {
    return <Skeleton variant="text" />;
  }

  const sorted = sortWith(
    [ascend(prop("name")), ascend(prop("countryCode"))],
    subdivisions.data ?? [],
  );
  const stored = { name: region, countryCode: country };
  const match = find(option => isSameRegion(option, stored), sorted);
  // Keep a stored region that isn't in the list so it still displays
  const options =
    notBlank(region) && !match ? append(stored, sorted) : sorted;
  const value = notBlank(region) ? (match ?? stored) : null;

  return (
    <Autocomplete
      id="submission-region"
      disableClearable
      disableListWrap
      options={options}
      value={value}
      filterOptions={filterOptions}
      getOptionLabel={regionLabel}
      isOptionEqualToValue={isSameRegion}
      onChange={(_event, option) => {
        if (option?.code) {
          onChange(option);
        }
      }}
      ListboxComponent={VirtualizedListbox}
      ListboxProps={{ listStyle: LISTBOX_BORDER_STYLE }}
      renderInput={params => (
        <TextField {...params} label="Region" variant="standard" fullWidth />
      )}
    />
  );
};

export default SubmissionRegionSelect;
